import SendIcon from '@mui/icons-material/Send';
import { Box, IconButton, TextField } from '@mui/material';
import { KeyboardEvent, TouchEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { gradient } from './assistant-theme';

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  /** True while a reply is pending: nothing can be sent until it arrives. */
  isSending: boolean;
  /** Puts the cursor in the box right away; left off on a phone, where it would open the keyboard. */
  autoFocus: boolean;
}

export default function MessageInput({
  value,
  onChange,
  onSend,
  isSending,
  autoFocus,
}: MessageInputProps) {
  const { t } = useTranslation();

  // iOS scrolls the page by the height of the keyboard to show the field, and the layout, which follows the visible
  // area, jumps ahead of that scroll. Focusing the field here keeps the page where it is.
  const onTouchEnd = (event: TouchEvent<HTMLTextAreaElement>) => {
    const field = event.currentTarget;

    if (document.activeElement !== field) {
      event.preventDefault();
      field.focus({ preventScroll: true });
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSend();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        px: 1.5,
        pt: 1.5,
        pb: 1,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={4}
        size="small"
        autoFocus={autoFocus}
        value={value}
        placeholder={t('ASSISTANT_PLACEHOLDER')}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        slotProps={{ htmlInput: { onTouchEnd } }}
      />
      <IconButton
        aria-label={t('ASSISTANT_SEND')}
        disabled={isSending || !value.trim()}
        onClick={onSend}
        // Pressing the button must not take the focus from the field, or the keyboard closes.
        onMouseDown={(event) => event.preventDefault()}
        sx={(theme) => ({
          borderRadius: 1,
          color: 'primary.contrastText',
          background: gradient(theme),
          '&:hover': { background: gradient(theme), opacity: 0.9 },
          '&.Mui-disabled': {
            background: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
          },
        })}
      >
        <SendIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
